import React from 'react';
import { Pen, Trash } from 'react-bootstrap-icons';
import { AlertCircle, AlertTriangle, Briefcase, Building2, Calendar, CalendarDays, ChartLine, CheckCircle, CircleCheckBig, CircleX, ClipboardCheck, Clock, FileCheck, FileText, FileX, Landmark, MessageSquare, Pencil, RefreshCw, Sun, Users } from 'lucide-react';
import moment from 'moment';

export const SUBMIT_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  FAILED: 'failed'
};

// Filter operator constants
export const FILTER_OPERATORS = {
  IS: 'Is',
  IS_NOT: 'Is not',
  IN_BETWEEN: 'In Between',
  AFTER: 'After',
  BEFORE: 'Before',
  TODAY_AND_BEFORE: 'Today and Before'
};

export const UNAUTHENTICATED_ROUTES = ['new-order'];
export const BRAND_NAME = 'Accurate Lien Searches';

export const SCRIPT_PROD_URL =
  'https://script.google.com/macros/s/AKfycbwFoFEobeymG87JA_fBhneR4zP1vfrzaU7EEgtsgmEAAizRyoWoDVHRbSq1RGCz4V9A/exec';

export const API_URL = process.env.REACT_APP_API_BASE_URL;

export const localStorageFields = ['user-email', 'user-role', 'user-name'];
export const MAX_CHUNK_SIZE = 512 * 1024 * 1024; // 512MB

export const ADMIN_ROLE = 'Admin';
export const ORG_ADMIN_ROLE = 'Org Admin';
export const PROCESSOR_ROLE = 'Processor';
export const ROLE_ORG_STAFF = 'Org Staff';
export const SALES_PERSON_ROLE = 'Sales Person';

export const ALL_ROLES = [ADMIN_ROLE, ORG_ADMIN_ROLE, PROCESSOR_ROLE, SALES_PERSON_ROLE];

export const TASK_ROLES = [ADMIN_ROLE, PROCESSOR_ROLE, SALES_PERSON_ROLE];

export const ORG_ROLES = [ORG_ADMIN_ROLE, ROLE_ORG_STAFF];

export const ACCURATE_ROLES = [ADMIN_ROLE, PROCESSOR_ROLE, SALES_PERSON_ROLE];

export const labelToKeyMap = {
  'Organization ID': 'organizationId',
  'Client Name': 'clientName',
  'Phone Number': 'phoneNumber'
};

export const SERVICES_RELATED_FILTERS = (users, organizations, municipalityOptions, countyOptions, statusOptions = null, isClientUser = false) => {
  // Helper function to get client status options (inline to avoid circular dependency)
  const getClientStatusOptions = statusOptions => {
    return Array.from(
      new Map(
        statusOptions
          .filter(opt => orderStatusForClientMap[opt.value])
          .map(opt => {
            const clientStatus = orderStatusForClientMap[opt.value];
            return [
              clientStatus,
              {
                label: clientStatus,
                value: clientStatusToOriginalOrderStatusMap[clientStatus]
              }
            ];
          })
      ).values()
    );
  };

  return [
    ...(statusOptions
      ? [
        {
          key: 'status',
          label: 'Status',
          values: [],
          options: isClientUser ? getClientStatusOptions(statusOptions) : statusOptions,
          type: 'checkbox',
          operatorOptions: [FILTER_OPERATORS.IS, FILTER_OPERATORS.IS_NOT],
          operator: FILTER_OPERATORS.IS
        }
      ]
      : []),
    {
      key: 'orderType',
      label: 'Order Type',
      values: [],
      options: [
        { label: 'Lien Search', value: 'Lien Search' },
        { label: 'Estoppel', value: 'Estoppel' },
        { label: 'Survey', value: 'Survey' }
      ],
      operatorOptions: [FILTER_OPERATORS.IS, FILTER_OPERATORS.IS_NOT],
      operator: FILTER_OPERATORS.IS,
      type: 'checkbox'
    },

    {
      key: 'assignedProcessors',
      label: 'Assigned Processors',
      values: [],
      options: users
        .filter(user => user.role === PROCESSOR_ROLE || user.role === ADMIN_ROLE)
        .map(user => ({ label: user.name, value: user._id })),
      type: 'checkbox',
      operatorOptions: [FILTER_OPERATORS.IS, FILTER_OPERATORS.IS_NOT],
      operator: FILTER_OPERATORS.IS,
      orderRelatedFilter: true,
      excludeForClientRole: true
    },
    {
      key: 'estimatedTurnAroundTime',
      label: 'ETA',
      values: {
        startDate: null,
        endDate: null
      },
      type: 'date',
      operatorOptions: [FILTER_OPERATORS.IN_BETWEEN, FILTER_OPERATORS.AFTER, FILTER_OPERATORS.BEFORE, FILTER_OPERATORS.TODAY_AND_BEFORE],
      operator: FILTER_OPERATORS.IN_BETWEEN,
      orderRelatedFilter: true
    },
    {
      key: 'isEtaPastClosingDate',
      label: 'ETA Flag',
      values: [],
      options: [
        {
          label: 'Yes',
          value: true
        },
        {
          label: 'No',
          value: false
        }
      ],
      type: 'checkbox',
      operatorOptions: [FILTER_OPERATORS.IS, FILTER_OPERATORS.IS_NOT],
      operator: FILTER_OPERATORS.IS,
      orderRelatedFilter: true
    },
    {
      key: 'cancellationRequested',
      label: 'Cancellation Requested',
      values: [],
      options: [
        {
          label: 'Yes',
          value: true
        },
        {
          label: 'No',
          value: false
        }
      ],
      type: 'checkbox',
      operatorOptions: [FILTER_OPERATORS.IS, FILTER_OPERATORS.IS_NOT],
      operator: FILTER_OPERATORS.IS,
      orderRelatedFilter: true
    },
    {
      key: 'organization',
      label: 'Organizations',
      values: [],
      operatorOptions: [FILTER_OPERATORS.IS, FILTER_OPERATORS.IS_NOT],
      operator: FILTER_OPERATORS.IS,
      options: organizations.map(p => ({ label: p.name, value: p._id })),
      type: 'checkbox',
      excludeForClientRole: true
    },
    {
      key: 'rush',
      label: 'Urgency',
      values: [],
      options: ['General', 'Rush'].map((p, i) => ({ label: p, value: i !== 0 })),
      type: 'checkbox',
      operatorOptions: [FILTER_OPERATORS.IS, FILTER_OPERATORS.IS_NOT],
      operator: FILTER_OPERATORS.IS,
      orderRelatedFilter: true,
      excludeForClientRole: true
    },
    {
      key: 'dateDelivered',
      label: 'Delivery Date',
      values: {
        startDate: null,
        endDate: null
      },
      type: 'date',
      operatorOptions: [FILTER_OPERATORS.IN_BETWEEN, FILTER_OPERATORS.AFTER, FILTER_OPERATORS.BEFORE],
      operator: FILTER_OPERATORS.IN_BETWEEN,
      orderRelatedFilter: true
    },
    {
      key: 'createdAt',
      label: 'Order Date',
      values: {
        startDate: null,
        endDate: null
      },
      type: 'date',
      operatorOptions: [FILTER_OPERATORS.IN_BETWEEN, FILTER_OPERATORS.AFTER, FILTER_OPERATORS.BEFORE],
      operator: FILTER_OPERATORS.IN_BETWEEN,
      orderRelatedFilter: true
    },
    {
      key: 'dateCancelled',
      label: 'Cancelled Date',
      values: {
        startDate: null,
        endDate: null
      },
      type: 'date',
      operatorOptions: [FILTER_OPERATORS.IN_BETWEEN, FILTER_OPERATORS.AFTER, FILTER_OPERATORS.BEFORE],
      operator: FILTER_OPERATORS.IN_BETWEEN,
      orderRelatedFilter: true
    },
    {
      key: 'closedFile',
      label: 'Paid Orders Date',
      values: {
        startDate: null,
        endDate: null
      },
      type: 'date',
      operatorOptions: [FILTER_OPERATORS.IN_BETWEEN, FILTER_OPERATORS.AFTER, FILTER_OPERATORS.BEFORE],
      operator: FILTER_OPERATORS.IN_BETWEEN,
      orderRelatedFilter: true
    },
    {
      key: 'closingDate',
      label: 'Closing Date',
      values: {
        startDate: null,
        endDate: null
      },
      type: 'date',
      operatorOptions: [FILTER_OPERATORS.IN_BETWEEN, FILTER_OPERATORS.AFTER, FILTER_OPERATORS.BEFORE],
      operator: FILTER_OPERATORS.IN_BETWEEN,
      orderRelatedFilter: false
    },
    {
      key: 'county',
      label: 'County',
      values: [],
      options: countyOptions?.map(item => ({ label: item, value: item })) || [],
      type: 'checkbox',
      operatorOptions: [FILTER_OPERATORS.IS, FILTER_OPERATORS.IS_NOT],
      operator: FILTER_OPERATORS.IS,
      orderRelatedFilter: false
    },
    {
      key: 'municipality',
      label: 'Municipality',
      values: [],
      options: municipalityOptions?.map(item => ({ label: item, value: item })) || [],
      type: 'checkbox',
      operatorOptions: [FILTER_OPERATORS.IS, FILTER_OPERATORS.IS_NOT],
      operator: FILTER_OPERATORS.IS,
      orderRelatedFilter: false
    }
  ];
};

export const datefields = [];

// User Profile Endpoints
// API RELATED CONSTANTS
export const ENDPOINTS = {
  //auth
  LOGIN: '/users/login',
  REGISTER: '/users/register',
  GENERATE_REGISTER_OTP: '/users/generate-registration-otp',
  FORGOT_PASSWORD: '/users/send-password-reset-link',
  VERIFY_EMAIL: '/users/verify-email',
  CHANGE_PASSWORD: '/users/change-password',
  RESET_PASSWORD: '/users/reset-password',
  REGISTER_WITH_GOOGLE: '/users/register-with-google',
  LOGIN_WITH_GOOGLE: '/users/login-with-google',
  GET_EMAIL_FROM_TEMPORARY_KEY: '/users/get-email-from-temporary-key',
  //users
  USERS_BASE: '/users/',
  USERS_ME: '/users/me',
  USERS_WITH_ID: id => `/users/${id}`,
  USERS_LIST: '/users/list',
  USERS_UPDATE: '/users/update-user-details',
  USERS_SEARCH: '/users/search',
  //organizations
  FETCH_ORGANIZATIONS: '/organizations/search',
  CREATE_ORGANIZATIONS: '/organizations',
  ORG_DASHBOARD_STATS: '/org-dashboard/stats',
  ORG_DASHBOARD_CANCELLED_STATS: '/org-dashboard/order-cancelled-stats',
  ORG_DASHBOARD_ORDER_STATS: '/org-dashboard/order-stats',
  ORG_DASHBOARD_CLOSED_FILES_LAST_MONTH: '/org-dashboard/closed-files-last-month',
  ORG_DASHBOARD_CLOSED_FILES_THIS_MONTH: '/org-dashboard/closed-files-this-month',
  ORG_DASHBOARD_UPCOMING_CLOSINGS: '/org-dashboard/upcoming-closings',
  ORG_DASHBOARD_ORDERS_NEEDING_ATTENTION: '/org-dashboard/orders-needing-attention',
  ORG_DASHBOARD_RECENT_ACTIVITY: '/org-dashboard/recent-activity',
  ORG_DASHBOARD_OUTSTANDING_INVOICE: '/org-dashboard/outstanding-invoice',
  ORGANIZATION_SUMMARY: '/org-dashboard/organization-summary',
  COUNTY_SUMMARY: '/org-dashboard/county-summary',
  ORDER_STATUS_BREAKDOWN: '/org-dashboard/order-status-breakdown',
  SEARCH_ORGANIZATION_EMAILS: '/organizations/search-emails',
  INVITE_STAFF: '/organizations/invite-staff',
  ORG_SIGN_UP: '/organizations/signup',
  ORG_ANALYTICS: '/org-dashboard/organization-analytics',
  FINANCIAL_INTELLIGENCE: '/financial-intelligence',
  FINANCIAL_INTELLIGENCE_PROFITABILITY_BY_CLIENT: '/financial-intelligence/profitability-by-client',
  //services/
  PUBLIC_CREATE_SERVICES: '/services/public',
  FETCH_SERVICES: '/services/search',
  CREATE_SERVICE: '/services',
  SERVICE_WITH_ID: id => `/services/${id}`,
  SERVICE_REQUEST_CHANGE: id => `/services/request-change/${id}`,
  // orders/
  FETCH_ORDERS_HOAS: '/orders/search/hoa',
  CREATE_ORDER: '/orders',
  ORDER_WITH_ID: id => `/orders/${id}`,
  ORDER_REQUEST_CANCELLATION: id => `/orders/request-cancellation/${id}`,
  ORDER_PAYMENT_REMINDER: id => `/orders/payment-reminder/${id}`,
  ORDER_EXPORT: '/orders/export',
  FETCH_DASHBOARD: '/orders/dashboard',
  FETCH_ORDERS: '/orders/search',
  PENDING_ORDERS_COUNT: '/orders/pending-count',
  ORDER_PIN: id => `/orders/${id}/pin`,
  ORDER_REPORT_INVOICES: orderId => `/orders/${orderId}/report-invoices`,

  SERVICE_VERSIONS: '/service-versions',
  SERVICE_VERSIONS_BY_ORDER: orderId => `/service-versions?orderId=${orderId}`,

  FETCH_TASKS: '/tasks/search',
  Create_TASKS: '/tasks',
  TASKS_WITH_ID: id => `/tasks/${id}`,
  QUALIA_MESSAGE_TEMPLATES: '/qualia-message-templates',
  QUALIA_MESSAGE_TEMPLATE_WITH_KEY: key => `/qualia-message-templates/${key}`,

  NOTIFICATIONS: '/notifications',
  NOTIFICATIONS_UNREAD_COUNT: '/notifications/unread-count',
  NOTIFICATIONS_READ_ALL: '/notifications/read-all',
  // invoice statements
  INVOICE_STATEMENTS: '/invoice-statements',
  INVOICE_STATEMENTS_EXPORT: '/invoice-statements/export',
  INVOICE_STATEMENT_WITH_ID: id => `/invoice-statements/${id}`,
  INVOICE_STATEMENT_MARK_PAID: id => `/invoice-statements/mark-paid/${id}`,
  INVOICE_STATEMENTS_RECONCILIATION: '/invoice-statements/reconciliation',
  INVOICE_STATEMENTS_RECONCILE: '/invoice-statements/reconcile',
  INVOICE_STATEMENTS_RECONCILIATION_IGNORE: '/invoice-statements/reconciliation-ignore',
  INVOICE_STATEMENTS_RECONCILIATION_HISTORY: '/invoice-statements/reconciliation-history',
  INVOICE_STATEMENTS_RECONCILIATION_HISTORY_BATCH: batchId =>
    `/invoice-statements/reconciliation-history/${batchId}`,
  NOTIFICATION_READ: id => `/notifications/${id}/read`,
  NOTIFICATION_DELETE: id => `/notifications/${id}`,

  COMMENTS: '/comments',
  COMMENT_WITH_ID: id => `/comments/${id}`,
  BILLING_NOTES: '/billing-notes',
  BILLING_NOTE_WITH_ID: id => `/billing-notes/${id}`,

  AUDIT_LOGS: '/audit-logs',

  // saved-filters
  SAVED_FILTERS: '/saved-filters',
  GET_SAVED_FILTERS: '/saved-filters/',
  DELETE_SAVED_FILTER: id => `/saved-filters/${id}`,
  UPDATE_SAVED_FILTER: id => `/saved-filters/${id}`,

  //files
  FILE: '/files',
  FILE_WITH_ID: id => `/files/${id}`,
  FILE_STREAM_WITH_ID: id => `/files/stream/${id}`,
  //misc
  APP_CHOICES_BASE: '/app-choices',
  APP_CHOICES_LIST: '/app-choices/list',
  APP_CHOICES_LIST_PUBLIC: '/app-choices/list/public',
  APP_CHOICES_WITH_ID: id => `/app-choices/${id}`,

  //reports
  REPORT_BASE: '/reports',
  FETCH_REPORTS: '/reports/search',
  REPORT_WITH_ID: id => `/reports/${id}`,
  GENERATE_REPORT: '/reports/generate-report',
  VALIDATE_REPORT: '/reports/validate-report',
  REPORT_AUTO_FILL_SECTION: '/reports/auto-fill-section',
  GENERATE_ESTOPPEL_REQUEST_LETTER: '/reports/generate-estoppel-request-letter',
  SHARE_REPORT: '/reports/share-report',

  // report QA issues (lien search)
  REPORT_QA_ISSUES_ANALYZE: '/report-qa-issues/analyze',
  REPORT_QA_ISSUES_GET: '/report-qa-issues/get',
  REPORT_QA_ISSUES_CREATE: '/report-qa-issues/create',
  REPORT_QA_ISSUES_UPDATE: id => `/report-qa-issues/update/${id}`,
  VALIDATE_REPORT_SUPPORTING_FILES: '/report-qa-issues/validate-supporting-files',
  REPORT_QA_RULESET: reportType => `/report-qa-rulesets/${encodeURIComponent(reportType)}`,
  REPORT_QA_RULESET_REWRITE: (reportType, sectionKey) =>
    `/report-qa-rulesets/${encodeURIComponent(reportType)}/sections/${encodeURIComponent(sectionKey)}/rewrite`,

  // estoppel report QA issues
  ESTOPPEL_REPORT_QA_ISSUES_ANALYZE: '/estoppel-report-qa-issues/analyze',
  ESTOPPEL_REPORT_QA_ISSUES_GET: '/estoppel-report-qa-issues/get',
  ESTOPPEL_REPORT_QA_ISSUES_CREATE: '/estoppel-report-qa-issues/create',
  ESTOPPEL_REPORT_QA_ISSUES_UPDATE: id => `/estoppel-report-qa-issues/update/${id}`,
  ESTOPPEL_VALIDATE_REPORT_SUPPORTING_FILES: '/estoppel-report-qa-issues/validate-supporting-files',

  // estoppel reports
  ESTOPPEL_REPORT_BASE: '/reports/estoppel',
  ESTOPPEL_REPORT_WITH_ID: id => `/reports/estoppel/${id}`,
  GENERATE_ESTOPPEL_REPORT: '/reports/estoppel/generate',
  SHARE_ESTOPPEL_REPORT: '/reports/estoppel/share-estoppel-report',

  // survey reports
  SURVEY_REPORT_BASE: '/reports/survey',
  SURVEY_REPORT_WITH_ID: id => `/reports/survey/${id}`,
  GENERATE_SURVEY_REPORT: '/reports/survey/generate',
  SHARE_SURVEY_REPORT: '/reports/survey/share-survey-report',

  //files
  FILE: '/files',
  BASE64_FILE: '/files/base64',
  BASE64_FILE_WITH_NAME: name => `/files/${name}`,
  FILE_WITH_ID: id => `/files/${id}`,
  FILE_STREAM_WITH_ID: id => `/files/stream/${id}`,

  //City-Department-Contacts
  CITY_DEPARTMENT_CONTACTS_SEARCH: '/city-department-contacts/search',
  CREATE_CITY_DEPARTMENT_CONTACTS: '/city-department-contacts',
  DEPARTMENT_CONTACTS_WITH_ID: id => `/city-department-contacts/${id}`,

  //Provider-Contacts
  PROVIDER_CONTACTS_SEARCH: '/provider-contacts/search',
  CREATE_PROVIDER_CONTACTS: '/provider-contacts',
  PROVIDER_CONTACTS_WITH_ID: id => `/provider-contacts/${id}`,

  // HOA directory
  ASSOCIATIONS_SEARCH: '/associations/search',
  ASSOCIATIONS: '/associations',
  ASSOCIATION_WITH_ID: id => `/associations/${id}`,
  ASSOCIATION_ORDERS: id => `/associations/${id}/orders`,
  ASSOCIATION_INSTRUCTION_FILES: id => `/associations/${id}/instruction-files`,
  ASSOCIATION_INSTRUCTION_FILE_DELETE: (id, fileId) => `/associations/${id}/instruction-files/${fileId}`,
  MANAGEMENT_COMPANIES_SEARCH: '/management-companies/search',
  MANAGEMENT_COMPANIES: '/management-companies',
  MANAGEMENT_COMPANY_WITH_ID: id => `/management-companies/${id}`,
  MANAGEMENT_COMPANY_INSTRUCTION_FILES: id => `/management-companies/${id}/instruction-files`,
  MANAGEMENT_COMPANY_INSTRUCTION_FILE_DELETE: (id, fileId) => `/management-companies/${id}/instruction-files/${fileId}`,
  CONTACTS_SEARCH: '/contacts/search',
  CONTACTS: '/contacts',
  CONTACT_WITH_ID: id => `/contacts/${id}`,

  //County-Department-Contacts
  COUNTY_DEPARTMENT_CONTACTS_SEARCH: '/county-department-contacts/search',
  CREATE_COUNTY_DEPARTMENT_CONTACTS: '/county-department-contacts',
  COUNTY_DEPARTMENT_CONTACTS_WITH_ID: id => `/county-department-contacts/${id}`,

  // turnaround times
  TURNAROUND_TIMES_SEARCH: '/turnaround-times/search',
  CREATE_TURNAROUND_TIMES: '/turnaround-times',
  TURNAROUND_TIMES_WITH_ID: id => `/turnaround-times/${id}`,

  // County/Municipality database (for appchoices citycounty & cities)
  COUNTY_MUNICIPALITIES_SEARCH: '/county-municipalities/search',
  COUNTY_CITY_OPTIONS: '/county-municipalities/county-city-options',
  CREATE_COUNTY_MUNICIPALITY: '/county-municipalities',
  COUNTY_MUNICIPALITY_WITH_ID: id => `/county-municipalities/${id}`,
  COUNTY_DEFAULT_PROCESSORS: '/county-municipalities/default-processors',
  DEFAULT_PROCESSORS_OF_COUNTY: county => `/county-municipalities/default-processors/${encodeURIComponent(county)}`,
  CREATE_COUNTY: '/county-municipalities/counties',
  COUNTY_WITH_NAME: county => `/county-municipalities/counties/${encodeURIComponent(county)}`,

  // google drive
  GOOGLE_DRIVE_LIST: '/google-drive/list',
  GOOGLE_DRIVE_READ_FOLDER: '/google-drive/read-folder',
  GOOGLE_DRIVE_UPLOAD: '/google-drive/upload',

  // workflows
  WORKFLOWS_BASE: '/workflows',

  // vnc
  VNC_SESSION_REQUEST: '/vnc/session/request',
  VNC_SESSION_STATUS: sessionId => `/vnc/session/${sessionId}`,
  VNC_SESSION_CONNECTION: sessionId => `/vnc/session/${sessionId}/connection`,
  VNC_SESSION_KEEPALIVE: sessionId => `/vnc/session/${sessionId}/keepalive`,
  VNC_SESSION_RELEASE: sessionId => `/vnc/session/${sessionId}`,
  VNC_POOL_STATUS: '/vnc/pool/status',

  // processing instructions
  PROCESSING_INSTRUCTIONS: '/processing-instructions',
  PROCESSING_INSTRUCTIONS_BY_MUNICIPALITY: (municipality, params = {}) => {
    const base = municipality
      ? `/processing-instructions/${encodeURIComponent(municipality)}`
      : '/processing-instructions';
    const searchParams = new URLSearchParams();
    if (params.county) {
      searchParams.set('county', params.county);
    }
    const query = searchParams.toString();
    return query ? `${base}?${query}` : base;
  },
  PROCESSING_INSTRUCTIONS_AUTOMATION: ({ county, orderId, reportMunicipality } = {}) => {
    const searchParams = new URLSearchParams();
    if (county) searchParams.set('county', county);
    if (orderId) searchParams.set('orderId', orderId);
    if (reportMunicipality) searchParams.set('reportMunicipality', reportMunicipality);
    const query = searchParams.toString();
    return query ? `/processing-instructions?${query}` : '/processing-instructions';
  },
  PROCESSING_INSTRUCTIONS_WITH_ID: id => `/processing-instructions/${id}`,
  PROCESSING_INSTRUCTION_UPLOAD_FILE: id => `/processing-instructions/${id}/file`,
  PROCESSING_INSTRUCTION_DELETE_FILE: (id, fileId) => `/processing-instructions/${id}/file/${fileId}`,
  PROCESSING_INSTRUCTION_PREVIEW_FILE: (id, fileId, mode = 'stream') =>
    `/processing-instructions/${id}/file/${fileId}/preview?mode=${encodeURIComponent(mode)}`,
  PROCESSING_INSTRUCTION_DOWNLOAD_FILE: fileId => `/processing-instructions/${fileId}/download`,

  // instruction builder
  INSTRUCTION_BUILDER_STEPS: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.county) searchParams.set('county', params.county);
    if (params.municipality) searchParams.set('municipality', params.municipality);
    if (params.showHidden !== undefined) searchParams.set('showHidden', params.showHidden);
    const query = searchParams.toString();
    return `/instruction-builder${query ? `?${query}` : ''}`;
  },
  INSTRUCTION_BUILDER_STEPS_UPDATE: '/instruction-builder',
  INSTRUCTION_BUILDER_STEP_FILES: id => `/instruction-builder/${id}/files`,
  INSTRUCTION_BUILDER_STEP_FILE: (id, fileId) => `/instruction-builder/${id}/files/${fileId}`,
  INSTRUCTION_BUILDER_STEP_FILE_PREVIEW: (id, fileId, mode = 'stream') =>
    `/instruction-builder/${id}/files/${fileId}/preview?mode=${encodeURIComponent(mode)}`,
  INSTRUCTION_BUILDER_STEP_FILE_DOWNLOAD: fileId => `/instruction-builder/${fileId}/download`,

  // server info (dev only)
  SERVER_INFO: '/server-info',

  //team-management
  HOLIDAYS: '/holidays',
  HOLIDAYS_LIST: '/holidays/list',
  HOLIDAYS_WITH_ID: id => `/holidays/${id}`,
  TIME_OFF_LOGS: '/time-off-logs',
  TIME_OFF_LOGS_LIST: '/time-off-logs/list',
  TIME_OFF_LOGS_WITH_ID: id => `/time-off-logs/${id}`,
  TIME_OFF_LOGS_WORK_DAYS: '/time-off-logs/work-days',
  TEAM_MANAGEMENT_STATS: '/org-dashboard/team-management-stats',

  // QuickBooks
  QUICKBOOKS_AUTH: '/quickbooks/auth',
  QUICKBOOKS_CHECK_CONNECTION: '/quickbooks/check-connection',
  QUICKBOOKS_SYNC_ORDER_HARDCOST: '/quickbooks/get-order-hardcost',
  QUICKBOOKS_STATEMENT_INVOICE: statementId => `/quickbooks/statement-invoice/${statementId}`,
};

export const countyCity = {
  'Miami Dade': [
    'Miami',
    'Miami Beach',
    'Coral Gables',
    'Hialeah',
    'Miami Springs',
    'North Miami',
    'North Miami Beach',
    'Opa Locka',
    'South Miami',
    'Homestead',
    'Miami Shores',
    'Bal Harbour',
    'Bay Harbor Islands',
    'Surfside',
    'West Miami',
    'Florida City',
    'Biscayne Park',
    'El Portal',
    'Golden Beach',
    'Pinecrest',
    'Indian Creek',
    'Medley',
    'North Bay Village',
    'Key Biscayne',
    'Sweetwater',
    'Virginia Gardens',
    'Hialeah Gardens',
    'Aventura',
    'Islandia',
    'Unincorporated Miami Dade county',
    'Sunny Isles Beach',
    'Miami Lakes',
    'Palmetto Bay',
    'Miami Gardens',
    'Doral',
    'Cutler Bay'
  ],
  Broward: [
    'Coconut Creek',
    'Cooper City',
    'Coral Springs',
    'Dania Beach',
    'Davie',
    'Deerfield Beach',
    'Fort Lauderdale',
    'Hallandale Beach',
    'Hillsboro Beach',
    'Hollywood',
    'Lauderdale By the Sea',
    'Lauderdale Lakes',
    'Lauderhill',
    'Lazy lake',
    'Lighthouse point',
    'Margate',
    'Miramar',
    'North Lauderdale',
    'Oakland Park',
    'Parkland',
    'Pembroke Park',
    'Pembroke Pines',
    'Plantation',
    'Pompano',
    'Sea Ranch Lakes',
    'Southwest Ranches',
    'Sunrise',
    'Tamarac',
    'Unicorporated Broward',
    'West Park',
    'Weston',
    'Wilton Manors'
  ],
  'Palm Beach': [
    'Atlantis',
    'Belle Glade',
    'Boca Raton',
    'Boyton Beach',
    'Briny Breeze',
    'Cloud Lake',
    'Delray Beach',
    'Glen Ridge',
    'Greenacres',
    'Gulf Stream',
    'Haverhill',
    'Highland Beach',
    'Hypoluxo',
    'Juno Beach',
    'Jupiter',
    'Jupiter Inlet',
    'Lake Clark Shores',
    'Lake Park',
    'Lake Worth',
    'Lantana',
    'Manalapan',
    'Mangonia Park',
    'North Palm Beach',
    'Ocean Ridge',
    'Pahokee',
    'Palm Beach (Town)',
    'Palm Beach Gardens',
    'Palm Beach Shores',
    'Palm Springs',
    'Riviera Beach',
    'Royal Palm Beach',
    'South Bay',
    'South Palm Beach',
    'Tequesta',
    'Unicorporated PBC',
    'Village of Golf',
    'Wellington',
    'West Palm Beach',
    'Jupiter Inlet Colony',
    'Loxahatchee Groves'
  ],
  Alachua: ['Alachua', 'Gainesville', 'Hawthorne', 'Archer', 'High Springs', 'La Crosse', 'Micanopy', 'Newberry', 'Waldo'],
  Baker: ['Glen Saint Mary', 'Macclenny'],
  Bay: ['Panama city', 'Callaway', 'Cedar Grove', 'Lynn Haven', 'Mexico Beach', 'Panama City Beach', 'Parker', 'Springfield', 'Youngstown'],
  Bradford: ['Brooker', 'Hampton Beach', 'Lawtey', 'Starke'],
  Brevard: [
    'Cape Cañaveral',
    'Cocoa',
    'Cocoa Beach',
    'Indialantic',
    'Melbourne',
    'Melbourne Beach',
    'Palm Bay',
    'Rockledge',
    'Satellite Beach',
    'Titusville',
    'West Melbourne',
    'Grant-Valkaria',
    'Indian Harbour Beach',
    'Malabar',
    'Melbourne Village',
    'Palm Shores'
  ],
  Calhoun: ['Bloutstown', 'Altha'],
  Charlotte: ['Punta Gorda'],
  Citrus: ['Crystal River', 'Inverness'],
  Clay: ['Green Cove Springs', 'Orange Park', 'Keystone Heights', 'Penney Farms'],
  Collier: ['Everglades city', 'Marco Island', 'Naples'],
  Columbia: ['Fort White', 'Lake City'],
  Desoto: ['Arcadia'],
  Dixie: ['Cross City', 'Horseshoe Beach'],
  Duval: ['Atlantic Beach', 'Jacksonville', 'Neptune Beach', 'Jacksonville Beach', 'Baldwin'],
  Escambia: ['Pensacola', 'Century'],
  Flagler: ['Palm Coast', 'Beverly Beach', 'Bunnell', 'Flagler Beach', 'Marineland'],
  Franklin: ['Apalachicola', 'Carrabelle'],
  Gadsden: ['Chattahoochee', 'Greensboro', 'Gretna', 'Havana', 'Midway', 'Quincy'],
  Gilchrist: ['Bell', 'Fanning Springs', 'Trenton'],
  Glades: ['Moore Haven'],
  Gulf: ['Port St. Joe'],
  Hamilton: ['Jasper', 'Jennings', 'White Springs'],
  Hardee: ['Bowling Green', 'Wauchula', 'Zolfo Springs'],
  Hendry: ['Clewiston', 'Labelle'],
  Hernando: ['Brooksville', 'Weeki Wachee'],
  Highlands: ['Avon Park', 'Lake Placid', 'Sebring'],
  Hillsborough: ['Plant city', 'Tampa', 'Temple Terrace', 'Sun City Center'],
  Holmes: ['Bonifay', 'Esto', 'Noma', 'Ponce De Leon', 'Westville'],
  'Indian River': ['Vero Beach', 'Sebastian', 'Fellsmere', 'Indian River Shores', 'Orchid'],
  Jackson: ['Alford', 'Bascom', 'Campbellton', 'Cottondale', 'Graceville', 'Grand Ridge', 'Greenwood', 'Jacob', 'Malone', 'Marianna', 'Sneads'],
  Jefferson: ['Monticello'],
  Lafayette: ['Mayo'],
  Lake: ['Eustis', 'Fruitland Park', 'Lady Lake', 'Leesburg', 'Minneola', 'Mount Dora', 'Tavares', 'Astatula', 'Clermont', 'Groveland', 'Howey-in-the-Hills', 'Mascotte', 'Montverde', 'Umatilla'],
  Lee: ['Bonita Springs', 'Cape Coral', 'Fort Myers', 'Fort Myers Beach', 'Sanibel', 'Unincorporated Lee'],
  Leon: ['Tallahassee'],
  Levy: ['Bronson', 'Cedar Key', 'Chiefland', 'Fanning Springs', 'Inglis', 'Otter Creek', 'Williston', 'Yankeetown'],
  Liberty: ['Bristol'],
  Madison: ['Greenville', 'Lee', 'Madison'],
  Manatee: ['Anna Maria Island', 'Bradenton', 'Holmes Beach', 'Longboat Key', 'Palmetto', 'Bradenton Beach'],
  Marion: ['Ocala', 'Belleview', 'Dunnellon', 'McIntosh', 'Reddick'],
  Martin: ["Sewall's Point", 'Stuart', 'Jupiter Island', 'Ocean Breeze Park', 'Indiantown'],
  Monroe: ['Islamorada', 'Key Largo', 'Key West', 'Key Colony Beach', 'Layton', 'Marathon'],
  Nassau: ['Callahan', 'Fernandina Beach', 'Hilliard'],
  Okaloosa: ['Cinco Bayou', 'Destin', 'Fort Walton Beach', 'Niceville', 'Shalimar', 'Valparaiso', 'Crestview', 'Laurel Hill', 'Mary Esther'],
  Okeechobee: ['Okeechobee'],
  Orange: ['Apopka', 'Edgewood', 'Maitland', 'Ocoee', 'Orlando', 'Winter Garden', 'Winter Park', 'Bay Lake', 'Belle Isle', 'Eatonville', 'Lake Buena Vista', 'Oakland', 'Windermere'],
  Osceola: ['Celebration', 'Kissimmee', 'St. Cloud'],
  Pasco: ['New Port Richey', 'Dade City', 'Port Richey', 'Saint Leo', 'San Antonio', 'Zephyrhills'],
  Pinellas: [
    'Clearwater',
    'Dunedin',
    'Belleair',
    'Gulfport',
    'Largo',
    'Oldsmar',
    'Pinellas Park',
    'St. Pete Beach',
    'St. Petersburg',
    'Safety Harbor',
    'Tarpon Springs',
    'Treasure Island',
    'Belleair Beach',
    'Belleair Bluffs',
    'Belleair Shore',
    'Indian Rocks Beach',
    'Indian Shores',
    'Kenneth City',
    'Madeira Beach',
    'North Redington Beach',
    'Redington Beach',
    'Redington Shores',
    'Seminole',
    'South Pasadena'
  ],
  Polk: [
    'Auburndale',
    'Bartow',
    'Eagle Lake',
    'Fort Meade',
    'Haines City',
    'Lake Alfred',
    'Lakeland',
    'Lake Wales',
    'Winter Haven',
    'Davenport',
    'Dundee',
    'Frostproof',
    'Highland Park',
    'Hillcrest Heights',
    'Lake Hamilton',
    'Mulberry',
    'Polk City'
  ],
  Putnam: ['Palatka', 'Crescent City', 'Interlachen', 'Pomona Park', 'Welaka'],
  'Santa Rosa': ['Gulf Breeze', 'Milton', 'Jay'],
  Sarasota: ['Longboat Key', 'North Port', 'Sarasota', 'Venice'],
  Seminole: ['Altamonte Springs', 'Casselberry', 'Lake Mary', 'Longwood', 'Oviedo', 'Sanford', 'Winter Springs'],
  'St. Johns': ['St. Augustine', 'St. Augustine Beach', 'Hastings'],
  'St. Lucie': ['Fort Pierce', 'Port St. Lucie', 'St. Lucie Village'],
  Sumter: ['Bushnell', 'Center Hill', 'Coleman', 'Webster', 'Wildwood'],
  Suwannee: ['Branford', 'Live Oak'],
  Taylor: ['Perry'],
  Union: ['Lake Butler', 'Raiford', 'Worthington Springs'],
  Volusia: [
    'Dayntona Beach',
    'Deland',
    'Deltona',
    'Debary Edgewater',
    'Holly Hill',
    'New Smyrna Beach',
    'Ormond Beach',
    'Ponce Inlet',
    'Port Orange',
    'Daytona Beach Shores',
    'DeBary',
    'Edgewater',
    'Lake Helen',
    'Oak Hill',
    'Orange City',
    'Pierson',
    'South Daytona'
  ],
  Wakulla: ['Crawfordville', 'Sopchoppy', 'St. Marks'],
  Walton: ['DeFuniak Springs', 'Seaside', 'Freeport', 'Paxton'],
  Washington: ['Chipley', 'Caryville', 'Ebro', 'Sunny Hills', 'Vernon', 'Wausau']
};

//TODO cleanup, all are key value of multiple service types
export const indServiceMetas = [
  {
    key: 'lienSearch',
    name: 'Lien Search',
    requiredFields: [
      { key: 'assignedProcessor', label: 'Assigned Processor' },
      { key: 'hardCost', label: 'Hard Cost' },
      { key: 'processingFee', label: 'Processing Fee' }
    ]
  },
  {
    key: 'survey',
    name: 'Survey',
    requiredFields: [
      { key: 'assignedProcessor', label: 'Assigned Processor' },
      { key: 'hardCost', label: 'Hard Cost' },
      { key: 'processingFee', label: 'Processing Fee' }
    ]
  },
  {
    key: 'estoppel',
    name: 'Estoppel',
    requiredFields: [
      { key: 'assignedProcessor', label: 'Assigned Processor' },
      { key: 'hardCost', label: 'Hard Cost' },
      { key: 'processingFee', label: 'Processing Fee' }
    ]
  }
];

export const servicesKeyValue = [
  { key: 'all', name: 'File Overview' },
  { key: 'lienSearch', name: 'Lien Search' },
  { key: 'estoppel', name: 'Estoppel' },
  { key: 'survey', name: 'Survey' }
];

/** Maps order type query values (display name or servicesKeyValue key) to a tab entry. */
export const resolveServiceKeyValForOrderType = orderType => {
  if (orderType == null || orderType === '') return undefined;
  const raw = Array.isArray(orderType) ? orderType[0] : orderType;
  if (typeof raw !== 'string' || !raw.trim()) return undefined;
  const normalized = raw.trim();
  return (
    servicesKeyValue.find(v => v.name === normalized) ||
    servicesKeyValue.find(v => v.key === normalized)
  );
};

export const orderTypeOptions = [
  { name: 'Any', key: 'All' },
  { name: 'Lien Search', key: 'lienSearch' },
  { name: 'Estoppel', key: 'estoppel' },
  { name: 'Survey', key: 'survey' }
];

// Admin
export const ADMIN_ROLES = ['Org Admin', 'Admin', 'Org Processor'];

export const USER_ROLES_ON_ADMIN_SIDE = [];

export const USER_ROLE_ON_ORG_SIDE = [];

export const ADMIN_PAGES_VIEW_ACCORDING_TO_ROLE = {
  Admin: {
    labels: ['Team Management', 'Organizations', 'App Choices', 'Back Office', 'Financial Intelligence', 'Statement'],
    links: ['team-management', 'organizations', 'app-choices', 'back-office', 'financial-intelligence', 'statement']
  },
  'Org Admin': { labels: ['Users'], routes: ['/admin/users'], links: ['users'] },
  'Org Processor': { labels: [], routes: [] }
};

export const ROLES_TO_WHICH_ADMIN_SECTION_IS_NOT_VISIBLE = [PROCESSOR_ROLE, ROLE_ORG_STAFF, SALES_PERSON_ROLE];

export const getRows = (headers = [], excludedFields = []) => [
  {
    columns: headers
      .filter(h => !excludedFields.includes(h.key))
      .map(
        ({
          name,
          formTitle,
          key: id,
          type = 'text',
          formType,
          variant,
          label,
          showLabel,
          required = true,
          options,
          optionValues,
          colWidth = 6,
          ...rest
        }) => ({
          default: colWidth,
          field: {
            title: formTitle || name,
            id,
            key: id,
            type: formType || (type === 'boolean' ? 'checkbox' : type),
            required,
            variant,
            label,
            showLabel,
            options,
            optionValues,
            ...rest
          }
        })
      )
  }
];

export const userHeaders = roles => [
  { name: 'Name', key: 'name' },
  { name: 'Phone', key: 'phone', type: 'phone', formType: 'text', required: false },
  { name: 'Email', key: 'email', type: 'email', formType: 'text', variant: 'email' },
  {
    name: 'Role',
    key: 'role',
    type: 'dropdown',
    showAsBadge: true,
    options: roles,
    dependentElems: ['defaultProcessorFor'],
    showDependentOnArray: [PROCESSOR_ROLE, ADMIN_ROLE]
  },
  { name: 'Status', key: 'isEnabled', type: 'dropdown', options: ['Enable', 'Disable'], showAsBadge: true },
  {
    formType: 'checkbox-group',
    options: ['Lien Search', 'Estoppel', 'Survey'],
    optionValues: ['Lien Search', 'Estoppel', 'Survey'],
    name: 'Default Processor For',
    key: 'defaultProcessorFor',
    required: false,
    colWidth: 12,
    boxWidth: 4
  }
];

export const userAddRows = roles => getRows(userHeaders(roles), ['createdAt', 'isEnabled']);
export const userUpdateRows = roles => getRows(userHeaders(roles), ['email', 'createdAt']);
export const userSearchKeys = ['name', 'email', 'phone', 'role'];

export const BUSSINESS_SOURCE_OPTIONS = ['Staff', 'Qualia', 'Closers Choice', 'Referral'];

export const USER_CREATE_AND_UPDATE_CHECKBOXES_VALUE = [
  {
    key: 'LeanProcessor',
    label: 'Lean Processor'
  },
  {
    key: 'EstoppelProcessor',
    label: 'Estoppel Processor'
  },
  {
    key: 'SurveyProcessor',
    label: 'Survey Processor'
  }
];

export const organizationHeaders = (users = []) => [
  { name: 'Name', key: 'name', width: '250px', minWidth: '200px' },
  {
    name: (
      <span className="text-center">
        Fee <br />
        <span className=" tinyFont" style={{ display: 'inline-block' }}>
          {`(Lien Search)`}
        </span>
      </span>
    ),
    key: 'lienSearchProcessingFee',
    formType: 'number'
  },
  {
    name: (
      <span className="text-center">
        Fee <br />
        <span className=" tinyFont" style={{ display: 'inline-block' }}>
          {`(Estoppel)`}
        </span>
      </span>
    ),
    key: 'estoppelProcessingFee',
    formType: 'number'
  },
  {
    name: (
      <span className="text-center">
        Fee <br />
        <span className=" tinyFont" style={{ display: 'inline-block' }}>
          {`(Survey)`}
        </span>
      </span>
    ),
    key: 'surveyProcessingFee',
    formType: 'number'
  },
  { name: 'Business Source', key: 'businessSource', type: 'dropdown', options: [...BUSSINESS_SOURCE_OPTIONS] },
  {
    name: 'Account Manager',
    key: 'accountManager',
    type: 'dropdown',
    options: users.map(u => u.name),
    optionValues: users.map(u => u._id),
    required: false,
    valueRenderer: value => users.find(u => u._id === value)?.name
  },
  {
    name: 'Lead Generator',
    key: 'leadGenerator',
    type: 'dropdown',
    options: users.map(u => u.name),
    optionValues: users.map(u => u._id),
    required: false,
    labelKey: 'name',
    valueRenderer: value => users.find(u => u._id === value)?.name
  },
  {
    name: 'Sales Person',
    key: 'salesPerson',
    type: 'dropdown',
    options: users.filter(u => u.role === SALES_PERSON_ROLE).map(u => u.name),
    optionValues: users.filter(u => u.role === SALES_PERSON_ROLE).map(u => u._id),
    required: false,
    labelKey: 'name',
    valueRenderer: value => users.find(u => u._id === value)?.name
  },
  { name: 'Status', key: 'isEnabled', type: 'dropdown', options: ['Enable', 'Disable'], showAsBadge: true },
  {
    name: (
      <span className="text-center">
        Search Condominium <br />
        <span className="tinyFont"> (In Automations)</span>
      </span>
    ),
    key: 'searchCondominiumInAutomations',
    type: 'boolean',
    options: ['Yes', 'No']
  },
  {
    name: 'Emails',
    key: 'emails',
    type: 'tags-input',
    formType: 'tags-input',
    placeholder: 'Enter email address...',
    inputType: 'email',
    required: false,
    width: '200px'
  },
  {
    name: 'Last Activity',
    key: 'lastActivity',
    valueRenderer: value => (value ? moment(value).format('MMM DD, YYYY HH:mm') : '-')
  }
];

export const organizationAddRows = users => {
  const baseRows = getRows(organizationHeaders(users), ['createdAt', 'isEnabled', 'lastActivity']);
  return [
    ...baseRows,
    {
      columns: [
        {
          default: 6,
          field: {
            id: 'newAdmin.name',
            title: 'Admin Name',
            type: 'text',
            required: true
          }
        },
        {
          default: 6,
          field: {
            id: 'newAdmin.email',
            title: 'Admin Email',
            type: 'text',
            formType: 'email',
            required: true
          }
        }
      ]
    },
    {
      columns: [
        {
          default: 6,
          field: {
            id: 'newAdmin.phone',
            title: 'Admin Phone',
            type: 'text',
            required: false
          }
        }
      ]
    }
  ];
};
const organizationUpdateRows = users => getRows(organizationHeaders(users), ['createdAt', 'lastActivity']);

export const organizationSearchKeys = ['name', 'contactEmail', 'phone', 'accountManager', 'bussinessSource'];

export const adminEntities = (roleOptions, users) => [
  {
    appChoiceKey: 'user',
    name: 'Users',
    endpoint: '/users',
    route: '/users',
    tableHeaders: userHeaders(roleOptions).filter(h =>
      roleOptions.includes(ORG_ADMIN_ROLE) || roleOptions.includes(ROLE_ORG_STAFF)
        ? h.key !== 'defaultProcessorFor'
        : true
    ),
    addRows: userAddRows(roleOptions),
    updateRows: userUpdateRows(roleOptions),
    searchKeys: [...userSearchKeys],
    updateEndpoint: '/users/update-user-details'
  },
  {
    appChoiceKey: 'organization',
    name: 'Organizations',
    endpoint: '/organizations',
    route: '/organizations',
    tableHeaders: organizationHeaders(users),
    addRows: organizationAddRows(users),
    updateRows: organizationUpdateRows(users),
    searchKeys: [...organizationSearchKeys],
    updateEndpoint: '/organizations'
  }
];

export const userFormCompanyEntities = roles => {
  return [
    {
      appChoiceKey: 'user',
      name: 'Users',
      endpoint: '/users',
      route: '/users',
      tableHeaders: [...userHeaders(roles).filter(header => header.key !== 'defaultProcessorFor')], // For Users From Company There Will Be No (defaultProcessorFor) Field
      addRows: userAddRows(roles),
      updateRows: userUpdateRows(roles),
      searchKeys: [...userSearchKeys],
      updateEndpoint: '/users/update-user-details'
    }
  ];
};

export const cancellationReasonOptions = [
  'Ordered by mistake',
  'Dead Deal',
  'Duplicate service',
  'Client refuses to continue',
  'Other'
];

export const BUTTONS_FOR_DIFFERENT_ENTITIES = [
  {
    entityName: 'Users',
    buttons: [
      { label: 'Edit', icon: <Pencil size={14} />, varient: 'dark' },
      { label: 'Reinvite', icon: <RefreshCw size={14} />, varient: 'info' },
      { label: 'Enable', icon: <CircleCheckBig size={14} />, varient: 'success' },
      { label: 'Disable', icon: <CircleX size={14} />, varient: 'danger' }
    ],
    containsEnableOrDisableMode: true
  },
  {
    entityName: 'Organizations',
    buttons: [
      { label: 'Edit', icon: <Pencil size={14} />, varient: 'dark' },
      { label: 'Enable', icon: <CircleCheckBig size={14} />, varient: 'success' },
      { label: 'Disable', icon: <CircleX size={14} />, varient: 'danger' },
      {
        label: 'View Users',
        icon: <Users size={14} />,
        varient: 'info'
      },
      {
        label: 'View Dashboard',
        icon: <ChartLine size={14} />,
        varient: 'success'
      }
    ],
    containsEnableOrDisableMode: true
  },
  {
    entityName: 'Others',
    buttons: [
      { label: 'Edit', icon: <Pen size={10} />, varient: 'dark' },
      { label: 'Delete', icon: <Trash size={10} />, varient: 'danger' }
    ],
    containsEnableOrDisableMode: false
  }
];

export const enableDisableOrDelete = ['Enable', 'Disable', 'Delete'];

export const SERVICES_SORT_BY_OPTIONS = [
  { key: 'updatedAt', label: 'Last Date Updated' },
  {
    key: 'createdAt',
    label: 'Date Created'
  },
  { key: 'fileName', label: 'Alphabetical', direction: 1 },
  { key: 'closingDate', label: 'Closing Date', direction: 1 }
];

export const EmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Pagination_Per_Page = 200;

export const USA_STATES = [
  { name: 'FLORIDA', abbreviation: 'FL' },
  { name: 'ALABAMA', abbreviation: 'AL' },
  { name: 'ALASKA', abbreviation: 'AK' },
  { name: 'AMERICAN SAMOA', abbreviation: 'AS' },
  { name: 'ARIZONA', abbreviation: 'AZ' },
  { name: 'ARKANSAS', abbreviation: 'AR' },
  { name: 'CALIFORNIA', abbreviation: 'CA' },
  { name: 'COLORADO', abbreviation: 'CO' },
  { name: 'CONNECTICUT', abbreviation: 'CT' },
  { name: 'DELAWARE', abbreviation: 'DE' },
  { name: 'DISTRICT OF COLUMBIA', abbreviation: 'DC' },
  { name: 'FEDERATED STATES OF MICRONESIA', abbreviation: 'FM' },

  { name: 'GEORGIA', abbreviation: 'GA' },
  { name: 'GUAM', abbreviation: 'GU' },
  { name: 'HAWAII', abbreviation: 'HI' },
  { name: 'IDAHO', abbreviation: 'ID' },
  { name: 'ILLINOIS', abbreviation: 'IL' },
  { name: 'INDIANA', abbreviation: 'IN' },
  { name: 'IOWA', abbreviation: 'IA' },
  { name: 'KANSAS', abbreviation: 'KS' },
  { name: 'KENTUCKY', abbreviation: 'KY' },
  { name: 'LOUISIANA', abbreviation: 'LA' },
  { name: 'MAINE', abbreviation: 'ME' },
  { name: 'MARSHALL ISLANDS', abbreviation: 'MH' },
  { name: 'MARYLAND', abbreviation: 'MD' },
  { name: 'MASSACHUSETTS', abbreviation: 'MA' },
  { name: 'MICHIGAN', abbreviation: 'MI' },
  { name: 'MINNESOTA', abbreviation: 'MN' },
  { name: 'MISSISSIPPI', abbreviation: 'MS' },
  { name: 'MISSOURI', abbreviation: 'MO' },
  { name: 'MONTANA', abbreviation: 'MT' },
  { name: 'NEBRASKA', abbreviation: 'NE' },
  { name: 'NEVADA', abbreviation: 'NV' },
  { name: 'NEW HAMPSHIRE', abbreviation: 'NH' },
  { name: 'NEW JERSEY', abbreviation: 'NJ' },
  { name: 'NEW MEXICO', abbreviation: 'NM' },
  { name: 'NEW YORK', abbreviation: 'NY' },
  { name: 'NORTH CAROLINA', abbreviation: 'NC' },
  { name: 'NORTH DAKOTA', abbreviation: 'ND' },
  { name: 'NORTHERN MARIANA ISLANDS', abbreviation: 'MP' },
  { name: 'OHIO', abbreviation: 'OH' },
  { name: 'OKLAHOMA', abbreviation: 'OK' },
  { name: 'OREGON', abbreviation: 'OR' },
  { name: 'PALAU', abbreviation: 'PW' },
  { name: 'PENNSYLVANIA', abbreviation: 'PA' },
  { name: 'PUERTO RICO', abbreviation: 'PR' },
  { name: 'RHODE ISLAND', abbreviation: 'RI' },
  { name: 'SOUTH CAROLINA', abbreviation: 'SC' },
  { name: 'SOUTH DAKOTA', abbreviation: 'SD' },
  { name: 'TENNESSEE', abbreviation: 'TN' },
  { name: 'TEXAS', abbreviation: 'TX' },
  { name: 'UTAH', abbreviation: 'UT' },
  { name: 'VERMONT', abbreviation: 'VT' },
  { name: 'VIRGIN ISLANDS', abbreviation: 'VI' },
  { name: 'VIRGINIA', abbreviation: 'VA' },
  { name: 'WASHINGTON', abbreviation: 'WA' },
  { name: 'WEST VIRGINIA', abbreviation: 'WV' },
  { name: 'WISCONSIN', abbreviation: 'WI' },
  { name: 'WYOMING', abbreviation: 'WY' }
];

export const COMMON_STATUSES = [
  'New Order',
  'Information Requested',
  'Information Received',
  'Ready for QA',
  'Revision',
  'Pending To Pay',
  'Paid',
  'Cancelled',
  'Hold'
];

export const LIENSEARCH_STATUSES = ['Update'];
export const ESTOPPEL_STATUSES = ['Due Diligence', 'Link Sent', 'Update', 'Refund Requested', 'Refunded'];
export const SURVEY_STATUSES = ['Due Diligence', 'Link Sent'];

/** Internal statuses that clients always see as "Cancelled". */
export const CLIENT_CANCELLED_INTERNAL_STATUSES = ['Refund Requested', 'Refunded', 'Cancelled'];

export const orderStatusForClientMap = {
  'New Order': 'New Order',
  'Due Diligence': 'In Progress',
  'Information Requested': 'In Progress',
  'Preliminary Report': 'In Progress',
  'Information Received': 'In Progress',
  'Ready for QA': 'In Progress',
  Revision: 'In Progress',
  Pending: 'In Progress',
  'Pending To Pay': 'Completed/Unpaid',
  Paid: 'Completed/Paid',
  Cancelled: 'Cancelled',
  Hold: 'On Hold',
  Update: 'Update In Progress',
  'Link Sent': 'Prepayment Link Sent',
  'Refund Requested': 'Cancelled',
  Refunded: 'Cancelled'
};

/**
 * Client-facing order status label (display only).
 * While a cancellation is requested and the order is not yet Refund Requested /
 * Refunded / Cancelled, clients see "Cancellation Requested". Those three
 * internal statuses all display as "Cancelled".
 */
export const getClientFacingOrderStatus = (status, cancellationRequested = false) => {
  if (CLIENT_CANCELLED_INTERNAL_STATUSES.includes(status)) {
    return 'Cancelled';
  }
  if (cancellationRequested) {
    return 'Cancellation Requested';
  }
  const mapped = orderStatusForClientMap[status];
  return typeof mapped === 'string' ? mapped : status;
};

export const orderStatusForClientMapGroup = originalStatus => {
  const clientStatus = orderStatusForClientMap[originalStatus];

  if (!clientStatus) {
    return [];
  }

  return Object.entries(orderStatusForClientMap)
    .filter(([key, val]) => val === clientStatus)
    .map(([key]) => key);
};

export const ORDER_STATUS_RANGE = {
  LAST30DAYS: 'LAST30DAYS',
  MORETHAN30DAYS: 'MORETHAN30DAYS',
  LAST60DAYS: 'LAST60DAYS',
  LASTMONTH: 'LASTMONTH',
  THISMONTH: 'THISMONTH',
  PAST_CLOSING_DATE: 'PAST_CLOSING_DATE',
  AWAITING_CLOSING: 'AWAITING_CLOSING',
  UPCOMING_CLOSINGS: 'UPCOMING_CLOSINGS'
};

export const getStartDateAndEndDateByRange = range => {
  const thirtyDaysAgo = moment()
    .subtract(30, 'days')
    .startOf('day');

  if (range === ORDER_STATUS_RANGE.LAST30DAYS) {
    const startDate = thirtyDaysAgo.toISOString();
    const endDate = moment().toISOString();
    return { startDate, endDate };
  } else if (range === ORDER_STATUS_RANGE.MORETHAN30DAYS) {
    const startDate = moment('1970-01-01').toISOString();
    const endDate = moment(thirtyDaysAgo)
      .subtract(1, 'millisecond')
      .toISOString();
    return { startDate, endDate };
  } else if (range === ORDER_STATUS_RANGE.LAST60DAYS) {
    const startDate = moment()
      .subtract(60, 'days')
      .startOf('day')
      .toISOString();
    const endDate = moment().toISOString();
    return { startDate, endDate };
  } else if (range === ORDER_STATUS_RANGE.LASTMONTH) {
    const startDate = moment()
      .subtract(1, 'month')
      .startOf('month')
      .toISOString();
    const endDate = moment()
      .subtract(1, 'month')
      .endOf('month')
      .toISOString();
    return { startDate, endDate };
  } else if (range === ORDER_STATUS_RANGE.THISMONTH) {
    const startDate = moment()
      .startOf('month')
      .toISOString();
    const endDate = moment()
      .endOf('month')
      .toISOString();
    return { startDate, endDate };
  } else if (range === ORDER_STATUS_RANGE.PAST_CLOSING_DATE) {
    const startDate = moment('1970-01-01')
      .startOf('day')
      .toISOString();
    const endDate = moment()
      .startOf('day')
      .subtract(1, 'millisecond')
      .toISOString();
    return { startDate, endDate };
  } else if (range === ORDER_STATUS_RANGE.AWAITING_CLOSING) {
    const startDate = moment()
      .startOf('day')
      .toISOString();
    const endDate = moment('2099-12-31')
      .endOf('day')
      .toISOString();
    return { startDate, endDate };
  } else if (range === ORDER_STATUS_RANGE.UPCOMING_CLOSINGS) {
    const startDate = moment()
      .startOf('day')
      .toISOString();
    const endDate = moment('2099-12-31')
      .endOf('day')
      .toISOString();
    return { startDate, endDate };
  }
};

// reverse mapping of orderStatusForClientMap
export const clientStatusToOriginalOrderStatusMap = Object.entries(orderStatusForClientMap).reduce(
  (acc, [originalStatus, clientStatus]) => {
    if (!acc[clientStatus]) {
      acc[clientStatus] = [];
    }
    acc[clientStatus].push(originalStatus);
    return acc;
  },
  {}
);

export const STATUSES = {
  common: COMMON_STATUSES, //ARRAY OF STRINGS
  'Lien Search': LIENSEARCH_STATUSES,
  Estoppel: ESTOPPEL_STATUSES,
  Survey: SURVEY_STATUSES
};

export const COUNT_GROUPS = {
  Active: [
    'New Order',
    'Due Diligence',
    'Link Sent',
    'Update',
    'Information Requested',
    'Information Received',
    'Refund Requested',
    'Hold'
  ],
  Delivered: ['Pending To Pay', 'Paid'],
  Cancelled: ['Refunded', 'Cancelled']
};

export const orderTypeToColor = {
  'Lien Search': 'green-light',
  Estoppel: 'blue-light',
  Survey: 'yellow-light'
};

export const DEFAULT_ORDER_STATUS = 'New Order';
export const INDIVIDUAL_ORDERS = 'INDIVIDUAL_ORDERS';

// for currency input
export const MAX_DECIMAL_PLACES = 2;

// backend fields to exclude from form state comparison
export const BACKEND_FIELDS_TO_EXCLUDE = [
  '_id',
  '__v',
  'createdBy',
  'updatedBy',
  'orderId',
  'createdAt',
  'updatedAt',
  'generatedFiles',
  'aiResponses',
  'autoOrganizationRunning',
  'autoOrganizationStartedAt'
];

// order types
export const ORDER_TYPES = {
  LIEN_SEARCH: 'lienSearch',
  ESTOPPEL: 'estoppel',
  SURVEY: 'survey'
};

export const REPORT_GENERATION_MODES = {
  PREVIEW_AND_SHARE: 'preview-and-share',
  QUICK_PREVIEW: 'quick-preview'
};

// Google OAuth Constants
export const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
export const LOGIN_PATH = `${window.location.origin}/login`;
export const SET_PASSWORD_PATH = `${window.location.origin}/set-password`;
export const DISCOVERY_DOCUMENT_LINK = 'https://accounts.google.com/.well-known/openid-configuration';

// Back Office Constants
export const MOBILE_BREAKPOINT = 992;
export const EXPANDED_WIDTH = 260;
export const COLLAPSED_WIDTH = 72;

export const SIDE_BAR_FOOTER_TAB = {
  "manage-counties": {
    rowBg: 'rgba(240, 253, 250, 0.95)',
    rowHover: 'rgba(224, 242, 241, 0.98)',
    inset: 'inset 3px 0 0 #0d9488',
    iconTile: 'rgba(20, 184, 166, 0.22)',
    label: '#0f766e'
  },
  "quickbooks": {
    iconTile: 'rgba(147, 51, 234, 0.14)',
    label: '#6d28d9',
    rowBg: 'rgba(245, 243, 255, 0.95)',
    rowHover: 'rgba(237, 233, 254, 0.98)',
    inset: 'inset 3px 0 0 #7c3aed'
  },
  "quickbooks-connected": {
    iconTile: 'rgba(28, 181, 28, 0.22)',
    label: '#168D16',
    rowBg: 'rgba(22, 141, 22, 0.10)',
    rowHover: 'rgba(28, 181, 28, 0.16)',
    inset: 'inset 3px 0 0 #1CB51C'
  }
}

export const BACK_OFFICE_TAB_HEADINGS = {
  'processing-instructions': {
    title: 'Processing Instructions'
  },
  'contacts-city-department': {
    title: 'City department'
  },
  'contacts-county-department': {
    title: 'County department'
  },
  'contacts-provider': {
    title: 'Provider'
  },
  'turnaround-times': {
    title: 'Turnaround Times Management'
  },
  'hoa-directory': {
    title: 'HOA Directory'
  },
  'lien-search-qa-rules': {
    title: 'Lien Search QA Rules'
  },
  'estoppel-qa-rules': {
    title: 'Estoppel QA Rules'
  },
  'qualia-message-templates': {
    title: 'Qualia Message Templates'
  }
};

export const DEFAULT_BACK_OFFICE_ITEMS = [
  {
    id: 'processing-instructions',
    name: 'Processing Instructions',
    icon: FileText
  },
  {
    id: 'turnaround-times',
    name: 'Turnaround Times',
    icon: Clock
  },
  {
    id: 'contacts-city-department',
    name: 'City department',
    icon: Building2
  },
  {
    id: 'contacts-county-department',
    name: 'County department',
    icon: Landmark
  },
  {
    id: 'contacts-provider',
    name: 'Provider',
    icon: Briefcase
  },
  {
    id: 'hoa-directory',
    name: 'HOA Directory',
    icon: Building2
  },
  {
    id: 'lien-search-qa-rules',
    name: 'Lien Search QA Rules',
    icon: ClipboardCheck
  },
  {
    id: 'estoppel-qa-rules',
    name: 'Estoppel QA Rules',
    icon: FileCheck
  },
  {
    id: 'qualia-message-templates',
    name: 'Qualia Messages',
    icon: MessageSquare
  }
];

export const AUTOMATION_STEPS_INFO = (county = 'Miami-Dade') => {
  return {
    step1: {
      title: 'Confirm Property Information',
      description: 'Confirm Property Information (Property Appraiser). This step extracts the property information from the Property Appraiser website and stores it in the service and upload pdf report to google drive.'
    },
    step2: {
      title: 'Identify The Municipality',
      description: 'Identify the municipality and confirm who provides garbage and water/sewer services at the county level.'
    },
    step3: {
      title: 'Real Estate Taxes (County Taxes Website)',
      description: 'Download the latest tax bill and bill history from the Miami-Dade County Property Tax website and update the report fields.'
    },
    step4: {
      title: 'Business Tax Search (BT)',
      description: 'Check if the property has a Business Tax receipt, if it does, save the files to google drive and update the report fields.'
    },
    step5: {
      title: 'Tangible Tax Search',
      description: 'Check if there is Tangible Personal Property Tax associated with the property, update the report fields and upload the PDFs to Google Drive.'
    },
    step6: {
      title: 'Building Violations Identification',
      description: 'Search for building violations at the county level and document any open or expired violations associated with the property.'
    },
    step7: {
      title: 'Building Permits Search',
      description: 'Search for open building permits associated with the property using folio and address searches.',
      captchaSolver: true,
      manualStep: true
    },
    step8: {
      title: 'Identify Neighborhood Code Violations',
      description: 'Check for neighborhood and building code violations associated with the property.'
    },
    step9: {
      title: `Identify Code Enforcement Citations (${county} County)`,
      description: 'Search for open Code Enforcement citations associated with the property using both folio and address searches.'
    },
    step10: {
      title: `Identify Special Assessments (${county} County)`,
      description: 'Check for any special assessments associated with the property at the county level and upload the PDFs to Google Drive.',
      // Human-only confirmation substep (matches backend template name)
      // humanOrAutoSubSteps: ['step10_1', 'step10_2']
    },
    step11: {
      title: 'Step 11',
      description: ""
    },
    step12: {
      title: 'Step 12',
      description: ""
    },
    step13: {
      title: 'Step 13',
      description: ""
    }
  }
};

export const ORDER_TABLE_COLUMNS_DISABLED = [
  'pin',
  'propertyAddress',
  'status'
]


export const TASK_GROUP_KEYS = ['overdue', 'today', 'tomorrow', 'thisWeek', 'later'];
export const TASK_PAST_GROUP_KEY = 'pastTasks';

const tasksDefaultHeaderColors = {
  labelColor: '#333',
  countBg: '#f0f0f0',
  countColor: '#999'
};

export const TASK_GROUP_CONFIG = {
  overdue: {
    label: 'Overdue',
    icon: AlertTriangle,
    iconColor: '#991B1B',
    labelColor: '#991b1b',
    countBg: '#fee2e2',
    countColor: '#991b1b'
  },
  today: {
    label: 'Today',
    icon: Sun,
    iconColor: '#1E40AF',
    labelColor: '#1e40af',
    countBg: '#dbeafe',
    countColor: '#1e40af'
  },
  tomorrow: {
    label: 'Tomorrow',
    icon: Clock,
    iconColor: '#888',
    ...tasksDefaultHeaderColors
  },
  thisWeek: {
    label: 'This week',
    icon: Calendar,
    iconColor: '#888',
    ...tasksDefaultHeaderColors
  },
  later: {
    label: 'Later',
    icon: CalendarDays,
    iconColor: '#888',
    ...tasksDefaultHeaderColors
  },
  pastTasks: {
    label: 'Past',
    icon: Clock,
    iconColor: '#888',
    ...tasksDefaultHeaderColors
  }
};

export const STATUS_STYLES = {
  pending: {
    background: '#FFFEF7',
    border: '#FDE68A',
    titleColor: '#222',
    titleBold: true,
    titleStrikethrough: false,
    detailColor: '#6B7280',
    badgeBg: '#FFF7D6',
    badgeColor: '#9A6700',
    circleBorder: '#F4B740',
    filled: false
  },

  completed: {
    background: '#F7FFFB',
    border: '#A7F3D0',
    titleColor: '#6B7280',
    titleBold: false,
    titleStrikethrough: false,
    detailColor: '#6B7280',
    badgeBg: '#E7FFF3',
    badgeColor: '#0F766E',
    circleBorder: '#34D399',
    filled: true
  },

  overdue: {
    background: '#FFF7F7',
    border: '#FECACA',
    titleColor: '#222',
    titleBold: true,
    titleStrikethrough: false,
    detailColor: '#6B7280',
    overdueColor: '#B91C1C',
    badgeBg: '#FEE2E2',
    badgeColor: '#EF4444',
    circleBorder: '#F87171',
    filled: false
  }
};

export const INSTRUCTION_BUILDER_TABS = ['Taxes', 'Permits', 'Code', 'Liens', 'Utilities', 'None'];

export const INSTRUCTION_BUILDER_TAB_COLORS = {
  Taxes: { bg: '#0f766e1f', text: '#0f766e' },
  Permits: { bg: '#fef3c7', text: '#92400e' },
  Code: { bg: '#ede9fe', text: '#5b21b6' },
  Liens: { bg: '#fee2e2', text: '#991b1b' },
  Utilities: { bg: '#c2410c1f', text: '#c2410c' },
  None: { bg: '#f0f0f0', text: '#6b7280' }
};

export const BILLING_NOTE_ALLOWED_ORDER_STATUSES = [
  'Pending To Pay',
  'Paid',
  ...CLIENT_CANCELLED_INTERNAL_STATUSES
];

export const INVOICE_STATUS_CONFIG = {
  Unpaid: {
    style: {
      background: '#DBEAFE',
      color: '#1E40AF'
    }
  },
  Due: {
    style: {
      background: '#FEF3C7',
      color: '#92400E'
    },
    Icon: CheckCircle
  },
  Overdue: {
    style: {
      background: '#FEE2E2',
      color: '#991B1B'
    },
    Icon: AlertCircle
  },
  'Check Sent': {
    style: {
      background: '#DBEAFE',
      color: '#1E40AF'
    },
    Icon: CheckCircle
  },
  'Cancellation Requested': {
    style: {
      background: '#FB923C',
      color: '#fff'
    },
    Icon: FileX
  },
  Cancelled: {
    style: {
      background: '#BA3353',
      color: '#fff'
    },
    Icon: FileX
  }
};
